import type { ComputedRef, Ref } from 'vue'
import { ref, computed } from 'vue'

import { SfLog } from '@app/adapters/logger'
import * as ReportTemplatesApi from '@app/application/client_meetings/api/report-templates'
import { useSearchable } from '@app/shared/vue/composables/use-searchable'
import type {
  Report,
  AvailableReport,
} from '@app/application/client_meetings/types'

interface UseMeetingReportsReturn {
  currentReports: Ref<Report[]>
  filteredAvailableReports: ComputedRef<AvailableReport[]>
  reportSearchValue: Ref<string>
  reportDropdownOpen: Ref<boolean>
  searchFocusedAt: Ref<number | undefined>
  showAddReport: ComputedRef<boolean>
  addReportDisabled: ComputedRef<boolean>
  isReportInFlight: (id: number) => boolean
  toggleReportDropdown: () => void
  closeReportDropdown: () => void
  addReport: (item: AvailableReport) => Promise<void>
  removeReport: (report: Report) => Promise<void>
}

export default function useMeetingReports(
  initialReports: Report[],
  initialAvailableReports: AvailableReport[],
  editable: Ref<boolean | undefined>
): UseMeetingReportsReturn {
  const inFlightIds = ref<Set<number>>(new Set())
  const currentReports = ref<Report[]>([...initialReports])
  const currentAvailableReports = ref<AvailableReport[]>([
    ...initialAvailableReports,
  ])
  const reportDropdownOpen = ref<boolean>(false)
  const searchFocusedAt = ref<number | undefined>(undefined)

  const showAddReport = computed(() => !!editable.value)
  const addReportDisabled = computed(
    () => currentAvailableReports.value.length === 0
  )

  const { searchValue: reportSearchValue, results: filteredAvailableReports } =
    useSearchable<AvailableReport, AvailableReport>(currentAvailableReports, {
      prepareItem: (item) => item,
      fuzzySortOptions: { keys: ['title'] },
    })

  function isReportInFlight(id: number): boolean {
    return inFlightIds.value.has(id)
  }

  function toggleReportDropdown(): void {
    reportDropdownOpen.value = !reportDropdownOpen.value
    if (reportDropdownOpen.value) {
      reportSearchValue.value = ''
      searchFocusedAt.value = Date.now()
    }
  }

  function closeReportDropdown(): void {
    reportDropdownOpen.value = false
  }

  async function addReport(item: AvailableReport): Promise<void> {
    if (inFlightIds.value.has(item.id)) return
    inFlightIds.value.add(item.id)
    try {
      const result = await ReportTemplatesApi.add(item.id)
      if (!result) {
        SfLog.error('Failed to add report to meeting: empty response')
        return
      }
      currentReports.value.push(result)
      currentAvailableReports.value = currentAvailableReports.value.filter(
        (r) => r.id !== item.id
      )
      closeReportDropdown()
    } catch (error) {
      SfLog.error('Failed to add report to meeting', { error })
    } finally {
      inFlightIds.value.delete(item.id)
    }
  }

  async function removeReport(report: Report): Promise<void> {
    if (!report.meetingReportId || inFlightIds.value.has(report.id)) return
    inFlightIds.value.add(report.id)
    try {
      await ReportTemplatesApi.remove(report.meetingReportId)
      currentReports.value = currentReports.value.filter(
        (r) => r.id !== report.id
      )
      currentAvailableReports.value = [
        ...currentAvailableReports.value,
        { id: report.id, title: report.title },
      ]
    } catch (error) {
      SfLog.error('Failed to remove report from meeting', { error })
    } finally {
      inFlightIds.value.delete(report.id)
    }
  }

  return {
    currentReports,
    filteredAvailableReports,
    reportSearchValue,
    reportDropdownOpen,
    searchFocusedAt,
    showAddReport,
    addReportDisabled,
    isReportInFlight,
    toggleReportDropdown,
    closeReportDropdown,
    addReport,
    removeReport,
  }
}
